import { PIN_CODE_FOR_DELIVERY } from '@nykaa/delivery-shared/constants';
import {
  IS_REPEAT_USER,
  USER_AFFILIATE_STORAGE_KEY,
  USER_SEGMENT_EXPIRY,
} from '@nykaa/auth-shared/store/constants';
import { ADDRESS_ID_FOR_DELIVERY } from 'shared/layouts/Product/DeliverySection/constant';
import { pushEvent } from 'helpers/utils/dataLayer';
import { events } from 'constants/dataLayer';
import { logger } from 'logging';
import {
  FACEBOOK_LOGIN_COOKIE,
  GOOGLE_LOGIN_COOKIE,
} from 'store/smartLock/constants';
import { USER_DATA_STORAGE_KEY } from 'constants/cookies';
import { deleteCookie } from 'shared/helpers/utils/cookie';
import {
  clearDeliveryAddress,
  GEO_LAT,
  GEO_LONG,
} from 'shared/pages/SelectDeliveryLocation/constants';
import { localStorageService } from 'client/service/storage';
import {
  reactStorageKeys,
  LOGINTYPE,
  LOGIN_SUCCESS,
  SIGNUP_SUCCESS,
  LOGIN_ATTEMPT,
  PRIVE_TIER_KEY,
  IOC_VALUE,
} from 'constants/auth';
import { PAID_EARLY_ACCESS_KEY } from '@nykaa/auth-shared/store/constants';
import { UserDataLayer } from './types';
import { defaultState } from './reducer';
// import { USER_LOGOUT } from '../../../shared/constants/dataLayer';
import { getAuthSteps } from 'shared/helpers/auth';

const pushUser = (data: UserDataLayer) => {
  pushEvent(events.USER_DATA_FETCH, {
    user: data,
  });
};

interface User {
  email: string;
  id: string;
  firstName: string;
  vrnRegionEligibility?: string;
  vrnExpState?: string;
  isPro: boolean;
  groupId: string;
  priveBand: string;
}

export const trackUser = (user: User) => {
  const { email, id, firstName, groupId, isPro, priveBand } = user;

  pushUser({
    email,
    customerId: id,
    firstName,
    isLoggedIn: !!email,
    groupId,
    isPro,
    priveBand,
  });
};

export const getAuthFromStorage = () => {
  try {
    const reactConfigExpiryString = localStorageService.getItem(
      reactStorageKeys.CONFIG_EXPIRY
    );
    const reactConfigExpiry = parseInt(reactConfigExpiryString || '', 10);
    const expiryTime = new Date(reactConfigExpiry);
    const currentTime = new Date();

    if (expiryTime > currentTime) {
      const reactConfigData = localStorageService.getItem(
        reactStorageKeys.CONFIG
      );
      if (reactConfigData) {
        const parsedReactConfigData = JSON.parse(reactConfigData);
        if (parsedReactConfigData) {
          return parsedReactConfigData;
        }
      }
    } else {
      pushEvent('mp_reset');
      pushEvent(events.CONFIG_EXPIRED);
    }
  } catch (err) {
    logger.error(err, 'Error reading react config from storage');
  }

  return null;
};

export const storeAuthInStorage = (response: any) => {
  try {
    const currentTime = new Date().getTime();
    const timeoutPeriod = reactStorageKeys.EXPIRY_TIME;
    const expiryTime = new Date(currentTime + timeoutPeriod).getTime();
    const stringifiedResponse = JSON.stringify(response);
    localStorageService.setItem(reactStorageKeys.CONFIG, stringifiedResponse);
    localStorageService.setItem(
      reactStorageKeys.CONFIG_EXPIRY,
      `${expiryTime}`
    );
  } catch (err) {
    logger.info(err, 'Error Storing reactConfig');
  }
};

export const removeAuthFromStorage = () => {
  try {
    localStorageService.removeItem(reactStorageKeys.CONFIG);
    localStorageService.removeItem(USER_SEGMENT_EXPIRY);
    localStorageService.removeItem(USER_AFFILIATE_STORAGE_KEY);
    deleteCookie(IS_REPEAT_USER);
    deleteCookie(PRIVE_TIER_KEY);
    deleteCookie(IOC_VALUE);
    deleteCookie(PAID_EARLY_ACCESS_KEY);
  } catch (err) {
    logger.error(err, 'Error removeAuthFromStorage');
  }
};

export const updateCartCountInStorage = (cartCount = 0) => {
  const authDataInStorage = getAuthFromStorage();
  if (authDataInStorage) {
    try {
      const result = authDataInStorage?.data?.result;
      if (result) {
        authDataInStorage.data.result.cartItemsTotal = cartCount;
        storeAuthInStorage(authDataInStorage);
      }
    } catch (err) {
      logger.info(err, 'Error: updateCartCountInStorage');
    }
  }
};

export const addWishListedItemInStorage = (productId: string) => {
  const authDataInStorage = getAuthFromStorage();
  if (authDataInStorage) {
    try {
      const wishlistItems =
        authDataInStorage?.data?.result?.wishlistProducts || [];
      if (wishlistItems && Array.isArray(wishlistItems)) {
        const isItemInWishlist = wishlistItems.some(
          (item) => item === productId
        );
        if (!isItemInWishlist) {
          wishlistItems.push(productId);
          authDataInStorage.data.result.wishlistProducts = wishlistItems;
          storeAuthInStorage(authDataInStorage);
        }
      }
    } catch (err) {
      logger.info(err, 'Error: addWishListedItemInStorage');
    }
  }
};

export const removeWishListedItemInStorage = (productId: string) => {
  const authDataInStorage = getAuthFromStorage();
  if (authDataInStorage) {
    try {
      const wishlistItems = authDataInStorage?.data?.result?.wishlistProducts;
      if (wishlistItems && Array.isArray(wishlistItems)) {
        const filteredWishlistItems = wishlistItems.filter(
          (item) => item !== productId
        );
        authDataInStorage.data.result.wishlistProducts = filteredWishlistItems;
        storeAuthInStorage(authDataInStorage);
      }
    } catch (err) {
      logger.info(err, 'Error: removeWishListedItemInStorage');
    }
  }
};

/** Handles wishlist items updation in storage for ask nykaa onResume */
export const updateWishlistItemsInStorage = (wishlistIds: string[]) => {
  const authDataInStorage = getAuthFromStorage();
  if (authDataInStorage) {
    try {
      if (authDataInStorage.data && authDataInStorage.data.result) {
        authDataInStorage.data.result.wishlistProducts = wishlistIds;
        storeAuthInStorage(authDataInStorage);
      }
    } catch (err) {
      logger.info(err, 'Error: updateWishlistItemsInStorage');
    }
  }
};

export const getInitialAuthState = (requestCookies: any) => {
  let authState = defaultState;
  const {
    head_data_react: headDataReact,
    IS_REPEAT_USER: repeatUserIdentifier,
    [PRIVE_TIER_KEY]: priveBand,
    IOC_VALUE: existingIocValue,
    [PIN_CODE_FOR_DELIVERY]: pinCode,
    [PAID_EARLY_ACCESS_KEY]: paidEarlyAccess,
    [GEO_LAT]: geoLat,
    [GEO_LONG]: geoLong,
  } = requestCookies;
  const parsedHeadDataReact = JSON.parse(headDataReact || null);
  let isRepeatUser = false;
  if (repeatUserIdentifier) {
    isRepeatUser =
      repeatUserIdentifier && Number(repeatUserIdentifier) ? true : false;
  }
  if (parsedHeadDataReact) {
    authState = {
      ...authState,
      user: {
        ...authState.user,
        isPro: parsedHeadDataReact.nykaa_pro === 1,
        groupId: parsedHeadDataReact.group_id,
        isRepeatUser,
        priveBand,
        iocValue: existingIocValue,
        pinCode,
        paidEarlyAccess: paidEarlyAccess || null,
        geoLat: geoLat || null,
        geoLong: geoLong || null,
      },
    };
  } else if (pinCode || geoLat) {
    authState = {
      ...authState,
      user: {
        ...authState.user,
        pinCode,
        geoLat: geoLat || null,
        geoLong: geoLong || null,
      },
    };
  }

  return authState;
};

export const isRegisteredViaMobile = (emailId: string) => {
  const NYKAA_EMAIL_REGEX = new RegExp('^\\d{10}@mobile.nykaa.com$');
  return NYKAA_EMAIL_REGEX.test(emailId);
};

export const loadVerloop = (data: any) => {
  if (!data || typeof data !== 'object') {
    pushEvent(events.VERLOOP_LOAD);
    return;
  }
  const { hash = '', unqTxnId = '', phoneNo = '' } = data || {};
  pushEvent(events.VERLOOP_LOAD, {
    hash,
    unqTxnId,
    phoneNo,
  });
};

export const pushLogoutVerloop = () => pushEvent(events.VERLOOP_LOGOUT, {});
export const getCartCountFromStorage = () => {
  const authDataInStorage = getAuthFromStorage();
  if (authDataInStorage) {
    try {
      return authDataInStorage?.data?.result?.cartItemsTotal;
    } catch (err) {
      logger.info(err, 'Error: getCartCountFromStorage');
    }
  }
};

export const cleanUp = () => {
  // pushEvent(events.USER_LOGOUT);
  navigator.credentials.preventSilentAccess();
  deleteCookie(FACEBOOK_LOGIN_COOKIE);
  deleteCookie(GOOGLE_LOGIN_COOKIE);
  deleteCookie(USER_DATA_STORAGE_KEY);
  deleteCookie(PIN_CODE_FOR_DELIVERY);
  deleteCookie(ADDRESS_ID_FOR_DELIVERY);
  clearDeliveryAddress();
  localStorageService.removeItem('loginLocation');
  localStorageService.removeItem('oneTapShown');
  setTimeout(() => {
    location.reload();
  }, 1000);
};

export const setLoginLocation = (location: string) => {
  localStorageService.setItem('loginLocation', location);
};

export const googleLoginTracking = () => {
  if (
    typeof window !== 'undefined' &&
    localStorage.getItem('googleLoginAttempt') === 'true'
  ) {
    localStorage.removeItem(LOGIN_ATTEMPT);
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const isSignUp = urlParams.get('sign_up') === '1';
    const isLogin = urlParams.get('login') === '1';
    const googleOneTapLogin =
      urlParams.get('loginType') === 'googleOneLinkExperiment';
    if (isLogin || isSignUp) {
      if (getAuthSteps() === 2 || googleOneTapLogin) {
        if (isSignUp) {
          pushEvent(SIGNUP_SUCCESS, {
            loginType: googleOneTapLogin
              ? LOGINTYPE.GOOGLE_ONE_TAP
              : LOGINTYPE.GOOGLE,
          });
        } else {
          pushEvent(LOGIN_SUCCESS, {
            loginType: googleOneTapLogin
              ? LOGINTYPE.GOOGLE_ONE_TAP
              : LOGINTYPE.GOOGLE,
          });
        }
      }
      localStorage.removeItem('googleLoginAttempt');
    }
  }
};
